import * as React from 'react'
import styled from 'styled-components'
import Link from 'theme/atoms/Link'
import { ReactComponent as VegaSVG } from 'assets/brands/vega.svg'
import { ReactComponent as ErwinMSVG } from 'assets/brands/erwin-m.svg'
import { ReactComponent as PulsivaSVG } from 'assets/brands/pulsiva.svg'
import { ReactComponent as JobelineSVG } from 'assets/brands/jobeline.svg'
import { ReactComponent as LusiniSVG } from 'assets/brands/lusini.svg'
import { ReactComponent as LinkArrow } from 'assets/link-arrow.svg'
import theme from 'theme'

import useLazyImgSrc from 'hooks/useLazyImgSrc'
import { OptImg } from 'utils/imageOptimization'

type Props = {
  imgSrc: string | OptImg
  to: string
  logo: string
  name: string
  linkLabel: string
}

export default function Teaser(props: Props) {
  const svgObj = {
    vega: VegaSVG,
    erwinM: ErwinMSVG,
    pulsiva: PulsivaSVG,
    jobeline: JobelineSVG,
    lusini: LusiniSVG
  }
  const svgLoader = (brand: string) => {
    const Svg = svgObj[brand]
    if (!Svg) {
      throw new Error(`Unknown brand key: '${brand}'`)
    }
    return <Svg />
  }
  const [ref, imgUrl] = useLazyImgSrc<HTMLImageElement>(props.imgSrc, 100)
  return (
    <Wrapper ref={ref} className="Teaser" $imgsrc={imgUrl} to={props.to}>
      <div className="teaser-description">
        <div className="teaser-description-headline">
          {svgLoader(props.logo)}
          <div className="brand-name">{props.name}</div>
        </div>
        <div>
          <span data-cy-handle="link">
            {props.linkLabel} <LinkArrow />
          </span>
        </div>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled(Link)<{ $imgsrc: string }>`
  background-image: url('${(p) => p.$imgsrc}');
  display: block;
  position: relative;
  background-size: cover;
  background-position: center center;
  width: 100%;
  height: 100%;
  > .teaser-description {
    background: ${theme.colors.white};
    position: absolute;
    bottom: ${theme.spacing('l')};
    margin-right: ${theme.spacing('l')};
    min-width: 8.125rem;
    @media (min-width: ${theme.ms.SM}px) {
      padding: ${theme.spacing('xxs')} ${theme.spacing('s')};
      bottom: ${theme.spacing('m')};
      min-height: 5rem;
      min-width: 9.375rem;
    }
    @media (min-width: ${theme.ms.MD}px) {
      max-height: 1.25rem;
      min-width: 9.25rem;
    }
    @media (min-width: ${theme.ms.LG}px) {
      padding: ${theme.spacing('xs')} ${theme.spacing('xs')};
      max-height: 5rem;
      min-width: 9.375rem;
    }
    @media (min-width: ${theme.ms.XL}px) {
      padding: ${theme.spacing('m')} ${theme.spacing('s')};
      bottom: ${theme.spacing('m')};
      max-height: 6.875rem;
      min-width: 12.5rem;
    }
    > .teaser-description-headline {
      min-height: 2.5rem;
      display: flex;
      margin-bottom: ${theme.spacing('xxs')};
      @media (min-width: ${theme.ms.XL}px) {
        margin-bottom: ${theme.spacing('xs')};
      }
      align-items: flex-end;
      > svg {
        width: 1.875rem;
        @media (min-width: ${theme.ms.XL}px) {
          width: 2.875rem;
        }
        margin-right: 0.5rem;
      }
      > .brand-name {
        ${theme.ty('rc-base')}
        white-space: nowrap;
        margin-right: ${theme.spacing('xxs')};
        @media (min-width: ${theme.ms.XL}px) {
          ${theme.ty('rc-2xl')}
        }
      }
    }
    > div {
      margin-bottom: ${theme.spacing('xs')};
      margin-left: ${theme.spacing('xs')};
      > span {
        ${theme.ty('rc-s', 'bold')}
        color:${theme.colors.primary};
        @media (min-width: ${theme.ms.XL}px) {
          display: block;
          ${theme.ty('rc-base', 'bold')}
        }
        > svg {
          margin-bottom: -0.1875rem;

          color: ${theme.colors.primary};
          #Path-5,
          #Path-6 {
            color: ${theme.colors.primary};
            stroke: ${theme.colors.primary};
          }
          g {
            color: ${theme.colors.primary};
            stroke: ${theme.colors.primary};
            filter: none;
          }
        }
      }
    }
  }
`
